// CSV 取り込みパネルの「仕様」＝列の定義・エンドポイント・件数の見せ方。
//
// 仕入先／商品と仕入先の紐付け／発注点の3系統はどれも同じ4段
// （①ファイル選択 → ②列の対応付け → ③dry-run で上書き件数を確認 →
// ④上書きして取り込む）で進む。違いをこの仕様だけに閉じ込め、UI は
// CsvImportPanel 1つに集約する（3枚コピペしない）。
import { ApiError } from "../api/client";
import type {
  CsvApplyResultBase,
  CsvMapping,
  CsvPreview,
} from "../api/stocky_csv_import";
import { t } from "../lib/i18n";

/** Select の「（使わない）」＝この列は送らない。 */
export const UNUSED_COLUMN = "";

/** 取り込みの失敗をマーチャントに見せる文言へ。パネルと一括取り込みで共用する。 */
export function csvErrorMessage(error: unknown): string {
  // 422 の detail は backend が request locale で用意した完成文＝そのまま出す。
  //    翻訳するならサーバ側＝ここで訳語を推測して置換しない。
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return t("予期しないエラーが発生しました。時間をおいて、もう一度お試しください。");
}

export interface CsvImportField {
  /** サーバへ送る mapping のキー。 */
  key: string;
  /** Select のラベル（日本語。必須列は文言に「（必須）」を含める）。 */
  label: string;
  isRequired: boolean;
}

/**
 * SKU 以外の突合不能も、dry-run と本適用の両方で同じ件数を出す。
 * `items` は表示用に打ち切られ得るため、件数は必ず `count` を使う。
 */
export interface CsvImportDiagnostic {
  count: number;
  items: readonly string[];
  /** `{count}` を含む確認画面用テンプレート。 */
  confirmTemplate: string;
  /** `{count}` と `{values}` を含む結果画面用テンプレート。 */
  resultTemplate: string;
}

/**
 * 1系統ぶんの仕様。
 *
 * 🔴 固定の文字列（title / description / fields.label / readyHint /
 * overwriteNoun）は【日本語のまま＝EN 辞書のキー】として持ち、訳すのは描画時＝
 * CsvImportPanel 側の t() で行う（仕様は定数なので、宣言時に t() を呼ぶと
 * locale 確定より先に評価されて ja で焼き付く）。
 * summarize / resultTitle は関数＝呼ばれた時点で t() する（specs.ts 側で解決済み）。
 */
export interface CsvImportSpec<TResult extends CsvApplyResultBase> {
  /** DOM id と読み上げラベルの接頭辞（画面内で一意）。 */
  id: string;
  /** セクション見出し。ファイル入力・ボタンの読み上げ名にも使う。 */
  title: string;
  description: string;
  fields: readonly CsvImportField[];
  /**
   * Stocky のどの画面から書き出すか（マーチャントはここで必ず迷う）。
   *
   * 🔴 **実画面で確認できた系統にだけ書く。** 経路を推測で書くと、その画面を
   * 探して見つからない店が「自分のStockyには無い」と判断して移行をやめる。
   * 未確認の系統は undefined のまま＝案内を出さない方が誠実。
   */
  exportPath?: string;
  /** 画面で選ぶ実ファイル名。Stockyの既知出力だけに指定する。 */
  expectedFileName?: string;
  /** guessed_mapping が必要条件を満たすとき、列選択を詳細設定へ畳む。 */
  autoMapKnownColumns?: boolean;
  /**
   * 任意列のうち最低1つは選ばせたいとき（例: 発注点／リードタイム／MOQ の
   * どれも選ばずに取り込むと、何も起きないのに成功したように見える）。
   */
  atLeastOneOf?: readonly string[];
  /**
   * 取り込める状態かの判定を差し替える（既定は「必須列が全部＋atLeastOneOf の
   * どれか1つ」）。棚卸し履歴のように「キー列は2択のどちらか」かつ「数量列は
   * 3つのうち2つ以上」という条件は既定の形では書けないため、そこだけ委ねる。
   */
  isReady?: (isSelected: (key: string) => boolean) => boolean;
  /** 必須の対応付けが未完了のときに出す案内文。 */
  readyHint: string;
  preview: (fileBase64: string) => Promise<CsvPreview>;
  apply: (
    fileBase64: string,
    mapping: CsvMapping,
    options: {
      dryRun: boolean;
      expectedPlanToken?: string;
      /** retention-only plan の本適用に対する、別操作の明示同意。 */
      confirmRetentionOnly?: boolean;
      sourceImportId: string;
      sourceFileName: string;
      sourceMediaType: string;
    },
  ) => Promise<TResult>;
  /** 上書きされる既存データの呼び名（「既存の◯◯ N 件を上書きします」）。 */
  overwriteNoun: string;
  /** 上書き件数。0 より大きいと確認バナーが警告トーンになる。 */
  overwriteCount: (result: TResult) => number;
  /** 実際に書き込まれた件数。0 なら親へ取り込み完了を通知しない。 */
  writtenCount: (result: TResult) => number;
  /** 件数の内訳（dry-run の確認と取り込み結果の両方で出す）。 */
  summarize: (result: TResult) => string;
  /** 取り込み結果バナーの見出し。 */
  resultTitle: (result: TResult) => string;
  /** 一致する商品が無かった SKU（SKU 照合をしない系統は空配列）。 */
  unmatchedSkus: (result: TResult) => string[];
  /** Vendor など、SKU 以外の突合診断。 */
  diagnostics?: (result: TResult) => readonly CsvImportDiagnostic[];
}

/** サーバの推測を初期値にした対応付け（推測が無い列は「使わない」）。 */
export function initialMapping<TResult extends CsvApplyResultBase>(
  spec: CsvImportSpec<TResult>,
  guessed: Record<string, string | null>,
): Record<string, string> {
  return Object.fromEntries(
    spec.fields.map((field) => [field.key, guessed[field.key] ?? UNUSED_COLUMN]),
  );
}

/** 画面の対応付けを送信形へ。「使わない」は null（列名の空文字を送らない）。 */
export function toPayloadMapping<TResult extends CsvApplyResultBase>(
  spec: CsvImportSpec<TResult>,
  mapping: Record<string, string>,
): CsvMapping {
  return Object.fromEntries(
    spec.fields.map((field) => [field.key, mapping[field.key] || null]),
  );
}

/** 必須列（＋必要なら任意列のいずれか）が選ばれていて取り込める状態か。 */
export function isMappingReady<TResult extends CsvApplyResultBase>(
  spec: CsvImportSpec<TResult>,
  mapping: Record<string, string>,
): boolean {
  const isSelected = (key: string) =>
    (mapping[key] ?? UNUSED_COLUMN) !== UNUSED_COLUMN;
  if (spec.isReady !== undefined) return spec.isReady(isSelected);
  const hasRequired = spec.fields
    .filter((field) => field.isRequired)
    .every((field) => isSelected(field.key));
  if (!hasRequired) return false;
  if (spec.atLeastOneOf === undefined) return true;
  return spec.atLeastOneOf.some(isSelected);
}
